interface TouchState {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
}

const state: TouchState = { up: false, down: false, left: false, right: false };

export function getTouch(): Readonly<TouchState> {
  return state;
}

export function initTouchControls(): void {
  const pad = document.getElementById('dpad');
  const knob = document.getElementById('dpad-knob');
  if (!pad || !knob) return;

  const arrows = {
    up: pad.querySelector<HTMLElement>('.dpad-up'),
    down: pad.querySelector<HTMLElement>('.dpad-down'),
    left: pad.querySelector<HTMLElement>('.dpad-left'),
    right: pad.querySelector<HTMLElement>('.dpad-right'),
  };

  const KNOB_RADIUS = 32;
  const DEADZONE = 14;
  let activePointer: number | null = null;

  const setState = (up: boolean, down: boolean, left: boolean, right: boolean): void => {
    state.up = up;
    state.down = down;
    state.left = left;
    state.right = right;
    arrows.up?.classList.toggle('active', up);
    arrows.down?.classList.toggle('active', down);
    arrows.left?.classList.toggle('active', left);
    arrows.right?.classList.toggle('active', right);
  };

  const reset = (): void => {
    setState(false, false, false, false);
    knob.style.transform = 'translate(-50%, -50%)';
    pad.classList.remove('pressed');
  };

  const updateFromPoint = (clientX: number, clientY: number): void => {
    const rect = pad.getBoundingClientRect();
    const dx = clientX - (rect.left + rect.width / 2);
    const dy = clientY - (rect.top + rect.height / 2);
    const dist = Math.hypot(dx, dy);

    const maxOffset = rect.width / 2 - KNOB_RADIUS;
    const clamp = Math.min(dist, maxOffset);
    const kx = dist > 0 ? (dx / dist) * clamp : 0;
    const ky = dist > 0 ? (dy / dist) * clamp : 0;
    knob.style.transform = `translate(calc(-50% + ${kx}px), calc(-50% + ${ky}px))`;

    if (dist < DEADZONE) {
      setState(false, false, false, false);
      return;
    }

    // 8-direction wedges centered on the cardinals: each cardinal is active
    // across a 135° arc, so 45° diagonal wedges activate two adjacent cardinals.
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI; // 0° = right, 90° = down
    const right = angle > -67.5 && angle < 67.5;
    const down = angle > 22.5 && angle < 157.5;
    const left = angle > 112.5 || angle < -112.5;
    const up = angle < -22.5 && angle > -157.5;
    setState(up, down, left, right);
  };

  pad.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    activePointer = e.pointerId;
    try {
      pad.setPointerCapture(e.pointerId);
    } catch {
      /* best effort */
    }
    pad.classList.add('pressed');
    updateFromPoint(e.clientX, e.clientY);
  });
  pad.addEventListener('pointermove', (e) => {
    if (e.pointerId !== activePointer) return;
    e.preventDefault();
    updateFromPoint(e.clientX, e.clientY);
  });
  const release = (e: PointerEvent): void => {
    if (activePointer !== null && e.pointerId !== activePointer) return;
    e.preventDefault();
    activePointer = null;
    reset();
  };
  pad.addEventListener('pointerup', release);
  pad.addEventListener('pointercancel', release);
  pad.addEventListener('lostpointercapture', release);
}
